import { HHBox } from '@hinge-health/react-component-library';
import { Tab, Tabs } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { PARTNER_DATA_GATEWAY_ROUTES } from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';

export const MODULE_NAV_TEST_ID = 'pdg-module-nav';
export const MODULE_NAV_OVERVIEW_TEST_ID = 'pdg-module-nav-overview-tab';
export const MODULE_NAV_INGESTIONS_TEST_ID = 'pdg-module-nav-ingestions-tab';
export const MODULE_NAV_CONFIGURATIONS_TEST_ID =
  'pdg-module-nav-configurations-tab';
export const MODULE_NAV_LABEL = 'Partner Data Gateway sections';

type ModuleNavSection = 'overview' | 'ingestions' | 'configurations';

export const ModuleNav = (): JSX.Element => {
  const basePath = useBasePath();
  const { pathname } = useLocation();
  const { canViewIngestions, canViewConfigurations } = usePermissions();

  const ingestionsPath = `${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.ingestions}`;
  const configurationsPath = `${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.configurations}`;

  // First prefix match wins. Anything else under the module — the index
  // and provider detail — belongs to the overview.
  const activeSection: ModuleNavSection = pathname.startsWith(
    configurationsPath,
  )
    ? 'configurations'
    : pathname.startsWith(ingestionsPath)
      ? 'ingestions'
      : 'overview';

  const sections: Array<{
    section: ModuleNavSection;
    label: string;
    to: string;
    testId: string;
    visible: boolean;
  }> = [
    {
      section: 'overview',
      label: 'Overview',
      to: basePath || '/',
      testId: MODULE_NAV_OVERVIEW_TEST_ID,
      visible: canViewIngestions,
    },
    {
      section: 'ingestions',
      label: 'Ingestions',
      to: ingestionsPath,
      testId: MODULE_NAV_INGESTIONS_TEST_ID,
      visible: canViewIngestions,
    },
    {
      section: 'configurations',
      label: 'Configurations',
      to: configurationsPath,
      testId: MODULE_NAV_CONFIGURATIONS_TEST_ID,
      visible: canViewConfigurations,
    },
  ];

  const visibleSections = sections.filter(section => section.visible);

  // A section the operator cannot see must not leave a dangling selection
  // on the strip (MUI warns, and no tab would look active).
  const tabsValue = visibleSections.some(
    section => section.section === activeSection,
  )
    ? activeSection
    : false;

  return (
    <HHBox data-testid={MODULE_NAV_TEST_ID} paddingX={6} paddingTop={2}>
      <Tabs value={tabsValue} aria-label={MODULE_NAV_LABEL}>
        {visibleSections.map(section => (
          <Tab
            key={section.section}
            value={section.section}
            label={section.label}
            component={Link}
            to={section.to}
            data-testid={section.testId}
          />
        ))}
      </Tabs>
    </HHBox>
  );
};
